import { useEffect, useState } from "react";
import { useRouterState } from "@tanstack/react-router";

const STORAGE_KEY = "backland:lastInAppRoute";

// Known in-app paths we want to label nicely. Anything else falls back to "Backland".
const PATH_LABELS: Array<{ match: (p: string) => boolean; label: string }> = [
  { match: (p) => p === "/info", label: "Info" },
  { match: (p) => p === "/", label: "Home" },
  { match: (p) => p.startsWith("/c/"), label: "Backland" },
  { match: (p) => p.startsWith("/e/"), label: "Backland" },
];

export type InAppReferrer = { path: string; label: string };

function labelFor(path: string): string {
  for (const { match, label } of PATH_LABELS) {
    if (match(path)) return label;
  }
  return "Backland";
}

/**
 * Mount once at app root. Records the user's most recent non-blog in-app route
 * to sessionStorage so the blog can offer a "Back to {wherever}" link.
 */
export function InAppReferrerTracker() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (pathname.startsWith("/blog")) return;
    if (pathname.startsWith("/getting-here")) return;
    try {
      const entry: InAppReferrer = { path: pathname, label: labelFor(pathname) };
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
    } catch {
      // sessionStorage may be unavailable; ignore.
    }
  }, [pathname]);

  return null;
}

/**
 * Read the most recent non-blog in-app route. Returns null when the visitor
 * arrived cold (e.g. from a search result) and has not navigated within the app.
 */
export function useInAppReferrer(): InAppReferrer | null {
  const [referrer, setReferrer] = useState<InAppReferrer | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as InAppReferrer;
      if (parsed && typeof parsed.path === "string" && typeof parsed.label === "string") {
        setReferrer(parsed);
      }
    } catch {
      // ignore
    }
  }, []);

  return referrer;
}
